// pages/Secciones.js
import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Popconfirm,
  Badge,
  Statistic,
  Card,
  InputNumber,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  AppstoreOutlined,
  ExportOutlined,
  BarcodeOutlined,
  FileTextOutlined,
  DollarOutlined,
  OrderedListOutlined,
  SortAscendingOutlined
} from '@ant-design/icons';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Secciones = () => {
  // Estados
  const [secciones, setSecciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalType, setModalType] = useState('create');
  const [currentSeccion, setCurrentSeccion] = useState(null);
  const [form] = Form.useForm();
  const [searchText, setSearchText] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Estados para filtros y ordenación
  const [filteredInfo, setFilteredInfo] = useState({});
  const [sortedInfo, setSortedInfo] = useState({});

  // Estado para paginación
//   const [pagination, setPagination] = useState({
//     current: 1,
//     pageSize: 10,
//     total: 0,
//     showSizeChanger: true,
//     showQuickJumper: true,
//     showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} secciones`
//   });

  // Configuración de columnas de la tabla
  const columns = [
    {
      title: 'Código',
      dataIndex: 'Codigo',
      key: 'Codigo',
      width: 120,
      sorter: (a, b) => a.Codigo?.localeCompare(b.Codigo || '') || 0,
      sortOrder: sortedInfo.columnKey === 'Codigo' && sortedInfo.order,
      render: (codigo) => (
        <Tag 
          color="blue" 
          style={{ 
            fontWeight: 'bold', 
            fontSize: '14px',
            padding: '4px 8px'
          }}
        >
          {/* <BarcodeOutlined style={{ marginRight: 4 }} /> */}
          {codigo}
        </Tag>
      ),
      fixed: 'left',
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 250,
      sorter: (a, b) => a.Descripcion?.localeCompare(b.Descripcion || '') || 0,
      sortOrder: sortedInfo.columnKey === 'Descripcion' && sortedInfo.order,
      render: (descripcion) => (
        <div style={{ maxWidth: 250 }}>
          <Text strong style={{ fontSize: '13px' }}>
            {descripcion}
          </Text>
        </div>
      ),
    },
    {
      title: 'Código Contable',
      dataIndex: 'CodConta',
      key: 'CodConta',
      width: 150,
      sorter: (a, b) => a.CodConta?.localeCompare(b.CodConta || '') || 0,
      sortOrder: sortedInfo.columnKey === 'CodConta' && sortedInfo.order,
      render: (codConta) => (
        <Tag color="purple" style={{ fontFamily: 'monospace' }}>
          {codConta}
        </Tag>
      ),
    },
    {
      title: 'Valor Negativo',
      dataIndex: 'ValorNegativo',
      key: 'ValorNegativo',
      width: 130,
      render: (valorNegativo) => (
        valorNegativo ? (
          <Badge
            status="error"
            text="Sí"
            style={{ color: '#ff4d4f', fontWeight: 'bold' }}
          />
        ) : (
          <Badge
            status="success"
            text="No"
            style={{ color: '#52c41a', fontWeight: 'bold' }}
          />
        )
      ),
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.ValorNegativo || null,
      onFilter: (value, record) => record.ValorNegativo === value,
    },
    {
      title: 'Listar',
      dataIndex: 'Listar',
      key: 'Listar',
      width: 100,
      render: (listar) => (
        listar ? (
          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '20px' }} />
        ) : (
          <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
        )
      ),
      filters: [
        { text: 'Sí', value: true },
        { text: 'No', value: false },
      ],
      filteredValue: filteredInfo.Listar || null,
      onFilter: (value, record) => record.Listar === value,
    },
    {
      title: 'Acciones',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="Ver detalles">
            <Button
              type="default"
              icon={<EyeOutlined />}
              size="small"
              onClick={() => handleView(record)}
            />
          </Tooltip>
          
          <Tooltip title="Editar">
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            />
          </Tooltip>
          
          <Popconfirm
            title="¿Eliminar sección?"
            description="Esta acción eliminará la sección permanentemente"
            onConfirm={() => handleDelete(record.Codigo)}
            okText="Sí"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Eliminar">
              <Button
                danger
                icon={<DeleteOutlined />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Cargar datos iniciales
  useEffect(() => {
    fetchSecciones();
  }, []);

  const fetchSecciones = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Parámetros de la query
      const queryParams = {
        // page: params.current || pagination.current,
        // pageSize: params.pageSize || pagination.pageSize,
        search: searchText,
        sortField: params.sortField || sortedInfo.field || 'Codigo',
        sortOrder: params.sortOrder || (sortedInfo.order === 'descend' ? 'DESC' : 'ASC')
      };
      
      // Añadir filtros
      if (filteredInfo.ValorNegativo) {
        queryParams.ValorNegativo = filteredInfo.ValorNegativo[0];
      }
      if (filteredInfo.Listar) {
        queryParams.Listar = filteredInfo.Listar[0];
      }
      
      const response = await axios.get(`${BASE_URL}/api/secciones`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });

      const cleanedData = (response.data.data || []).map(item => ({
        ...item,
        Descripcion: item.Descripcion || '',
        CodConta: item.CodConta || '',
        ValorNegativo: Boolean(item.ValorNegativo),
        Listar: Boolean(item.Listar)
      }));
      
      setSecciones(cleanedData);
      
      // Actualizar paginación
    //   setPagination({
    //     ...pagination,
    //     current: response.data.pagination?.currentPage || 1,
    //     pageSize: response.data.pagination?.pageSize || pagination.pageSize,
    //     total: response.data.pagination?.totalRecords || cleanedData.length
    //   });
      
    } catch (error) {
      console.error('❌ Error cargando secciones:', error);
      message.error('Error al cargar las secciones');
      setSecciones([]);
    //   setPagination(prev => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

//   const handleTableChange = (newPagination, filters, sorter) => {
//     setFilteredInfo(filters);
//     setSortedInfo(sorter);
    
//     const params = {
//       current: newPagination.current,
//       pageSize: newPagination.pageSize,
//       sortField: sorter.field,
//       sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC'
//     };
    
//     setPagination(prev => ({
//       ...prev,
//       current: newPagination.current,
//       pageSize: newPagination.pageSize
//     }));
    
//     fetchSecciones(params);
//   };

  // Handlers para las acciones
  const handleCreate = async () => {
    setModalType('create');
    setCurrentSeccion(null);
    form.resetFields();
    
    // Establecer valores por defecto
    form.setFieldsValue({
      ValorNegativo: false,
      Listar: true
    });
    
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setModalType('edit');
    setCurrentSeccion(record);
    
    const formValues = {
      Codigo: record.Codigo,
      Descripcion: record.Descripcion || '',
      CodConta: record.CodConta || '',
      ValorNegativo: record.ValorNegativo || false,
      Listar: record.Listar || true
    };
    
    form.setFieldsValue(formValues);
    setModalVisible(true);
  };

  const handleView = (record) => {
    Modal.info({
      title: `Sección ${record.Codigo}`,
      width: 600,
      icon: <AppstoreOutlined />,
      content: (
        <div style={{ marginTop: 20 }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <strong>Código:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="blue" style={{ fontSize: '18px', padding: '6px 12px' }}>
                  <BarcodeOutlined style={{ marginRight: 8 }} />
                  {record.Codigo}
                </Tag>
              </div>
            </Col>
            
            <Col span={24}>
              <strong>Descripción:</strong>
              <div style={{ 
                marginTop: 4, 
                padding: '12px', 
                backgroundColor: '#f5f5f5', 
                borderRadius: '6px',
                fontSize: '14px'
              }}>
                {record.Descripcion || 'Sin descripción'}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Código Contable:</strong>
              <div style={{ marginTop: 4 }}>
                <Tag color="purple" style={{ fontSize: '14px', padding: '4px 8px' }}>
                  {record.CodConta}
                </Tag>
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Valor Negativo:</strong>
              <div style={{ marginTop: 4 }}>
                {record.ValorNegativo ? (
                  <Badge 
                    status="error" 
                    text="Sí" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                ) : (
                  <Badge 
                    status="success" 
                    text="No" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                )}
              </div>
            </Col>
            
            <Col span={12}>
              <strong>Listar:</strong>
              <div style={{ marginTop: 4 }}>
                {record.Listar ? (
                  <Badge 
                    status="success" 
                    text="Sí" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                ) : (
                  <Badge 
                    status="default" 
                    text="No" 
                    style={{ fontSize: '14px', fontWeight: 'bold' }}
                  />
                )}
              </div>
            </Col>
            
            <Col span={24}>
              <Divider />
              <Card size="small" style={{ backgroundColor: '#f6ffed' }}>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  <strong>💡 Información:</strong>
                  <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                    <li><strong>Valor Negativo:</strong> Indica si los valores de esta sección se consideran negativos en contabilidad</li>
                    <li><strong>Listar:</strong> Determina si esta sección aparece en los listados del sistema</li>
                    <li><strong>Código Contable:</strong> Código utilizado para la integración con el sistema contable</li>
                  </ul>
                </div>
              </Card>
            </Col>
          </Row>
        </div>
      ),
      onOk() {},
    });
  };

  const handleDelete = async (codigo) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/secciones/${codigo}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Filtrar el eliminado
      setSecciones(secciones.filter(s => s.Codigo !== codigo));
      
      // Actualizar total
    //   setPagination(prev => ({
    //     ...prev,
    //     total: prev.total - 1
    //   }));
      
      message.success('Sección eliminada correctamente');
    } catch (error) {
      console.error('❌ Error eliminando sección:', error);
      message.error(error.response?.data?.error || 'Error al eliminar la sección');
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');

      if (modalType === 'create') {
        const response = await axios.post(
          `${BASE_URL}/api/secciones`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        fetchSecciones();
        message.success('Sección creada correctamente');
      } else {
        const response = await axios.put(
          `${BASE_URL}/api/secciones/${currentSeccion.Codigo}`,
          values,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        // Actualizar el estado local
        const seccionActualizada = {
          ...response.data.data,
          ...values
        };
        
        setSecciones(prevSecciones => 
          prevSecciones.map(seccion => 
            seccion.Codigo === currentSeccion.Codigo
              ? seccionActualizada 
              : seccion
          )
        );
        
        message.success('Sección actualizada correctamente');
      }
      
      setModalVisible(false);
      form.resetFields();
      
    } catch (error) {
      console.error('❌ Error guardando sección:', error);
      message.error(error.response?.data?.error || 'Error al guardar los datos');
    }
  };

  const handleSearch = () => {
    if (searchText.trim() === '') {
      message.warning('Por favor ingrese un término de búsqueda');
      return;
    }
    setSearchLoading(true);
    // setPagination({ ...pagination, current: 1 });
    fetchSecciones({ current: 1 });
  };

  const handleResetSearch = () => {
    setSearchText('');
    setFilteredInfo({});
    setSortedInfo({});
    setSearchLoading(false);
    // setPagination({ ...pagination, current: 1 });
    fetchSecciones({ current: 1 });
  };

  const handleExport = () => {
    message.info('Función de exportación en desarrollo');
  };

  const searchBar = (
    <div style={{ marginBottom: 16 }}>
      <Space>
        <Input
          placeholder="Buscar por código, descripción, código contable..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 350 }}
          onPressEnter={handleSearch}
          allowClear
          onClear={handleResetSearch}
          prefix={<SearchOutlined />}
        />
        <Button
          type="primary"
          icon={<SearchOutlined />}
          onClick={handleSearch}
          loading={searchLoading}
        >
          Buscar
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleResetSearch}
        >
          Restablecer
        </Button>
        {/* <span style={{ color: '#666', fontSize: '12px' }}>
          {pagination.total.toLocaleString()} secciones
        </span> */}
      </Space>
    </div>
  );

  // Estadísticas
  const estadisticas = (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col span={6}>
        <Statistic 
          title="Total Secciones" 
          value={secciones.length}
          prefix={<AppstoreOutlined />}
          valueStyle={{ color: '#1890ff' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Activas" 
          value={secciones.filter(s => s.Listar).length}
          suffix={`/ ${secciones.length}`}
          prefix={<CheckCircleOutlined />}
          valueStyle={{ color: '#52c41a' }}
        />
      </Col>
      <Col span={6}>
        <Statistic 
          title="Valor Negativo" 
          value={secciones.filter(s => s.ValorNegativo).length}
          suffix={`/ ${secciones.length}`}
          prefix={<DollarOutlined />}
          valueStyle={{ color: '#fa8c16' }}
        />
      </Col>
      {/* <Col span={6}>
        <Statistic 
          title="Página" 
          value={pagination.current}
          suffix={`/ ${Math.ceil(pagination.total / pagination.pageSize)}`}
          prefix={<OrderedListOutlined />}
          valueStyle={{ color: '#722ed1' }}
        />
      </Col> */}
    </Row>
  );

  return (
    <>
      <PageContainer
        title="Secciones"
        subtitle={`${secciones.length} secciones registradas`}
        icon={<AppstoreOutlined />}
        loading={loading}
        onRefresh={() => fetchSecciones()}
        onCreate={handleCreate}
        badgeCount={secciones.length}
        badgeStatus="processing"
        extra={
          <Space>
            <Button
              icon={<ExportOutlined />}
              onClick={handleExport}
            >
              Exportar
            </Button>
            {/* <Tag color="blue" style={{ fontSize: 14, padding: '4px 12px', borderRadius: 20 }}>
              <strong>{pagination.total.toLocaleString()}</strong> secciones
            </Tag> */}
          </Space>
        }
        cardStyle={{
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        }}
      >
        {/* Estadísticas */}
        {estadisticas}
        
        {/* Barra de búsqueda */}
        {searchBar}
        
        {/* Tabla */}
        <Table
          columns={columns}
          dataSource={secciones}
          rowKey={(record) => record.Codigo}
          loading={loading}
        //   onChange={handleTableChange}
        //   pagination={pagination}
          scroll={{ x: 1200, y: 600 }}
          style={{
            borderRadius: '8px',
            overflow: 'hidden'
          }}
          size="middle"
          locale={{
            emptyText: 'No se encontraron secciones'
          }}
        />
      </PageContainer>

      {/* Modal para crear/editar sección */}
      <Modal
        title={modalType === 'create' ? 'Nueva Sección' : 'Editar Sección'}
        open={modalVisible}
        onOk={handleSave}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        width={600}
        okText={modalType === 'create' ? 'Crear' : 'Guardar'}
        cancelText="Cancelar"
        confirmLoading={loading}
        style={{ top: 20 }}
        bodyStyle={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            ValorNegativo: false,
            Listar: true
          }}
        >
          {/* Primera fila: Código y Descripción */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="Codigo"
                label="Código"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese el código',
                    max: 10,
                    whitespace: false
                  }
                ]}
                tooltip="Código único de la sección (máx. 10 caracteres)"
              >
                <Input 
                  placeholder="Ej: HAB, MIN, DES"
                  maxLength={10}
                  disabled={modalType === 'edit'}
                  prefix={<BarcodeOutlined />}
                />
              </Form.Item>
            </Col>
            <Col span={16}>
              <Form.Item
                name="Descripcion"
                label="Descripción"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese la descripción',
                    max: 100
                  }
                ]}
                tooltip="Descripción completa de la sección"
              >
                <Input 
                  placeholder="Descripción de la sección"
                  maxLength={100}
                  showCount
                  prefix={<FileTextOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Segunda fila: Código Contable */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="CodConta"
                label="Código Contable"
                rules={[
                  { 
                    required: true, 
                    message: 'Por favor ingrese el código contable',
                    pattern: /^\d{10}$/,
                    message: 'Debe tener exactamente 10 dígitos'
                  }
                ]}
                tooltip="Código de 10 dígitos para integración contable"
              >
                <Input 
                  placeholder="Ej: 7000000000, 7050000001"
                  maxLength={10}
                  prefix={<DollarOutlined />}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Tercera fila: Switches */}
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="ValorNegativo"
                label="Valor Negativo"
                valuePropName="checked"
                tooltip="Indica si los valores de esta sección se consideran negativos en contabilidad"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                  style={{ minWidth: 80 }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="Listar"
                label="Listar"
                valuePropName="checked"
                tooltip="Determina si esta sección aparece en los listados del sistema"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                  style={{ minWidth: 80 }}
                />
              </Form.Item>
            </Col>
          </Row>

          {modalType === 'create' && (
            <Card size="small" style={{ marginTop: 16, backgroundColor: '#f6ffed' }}>
              <div style={{ fontSize: '13px', color: '#666' }}>
                <strong>📝 Información importante:</strong>
                <ul style={{ marginTop: 8, marginBottom: 4, paddingLeft: 20 }}>
                  <li><strong>Código:</strong> Identificador único de la sección (ej: HAB, MIN, DES)</li>
                  <li><strong>Descripción:</strong> Nombre completo de la sección</li>
                  <li><strong>Código Contable:</strong> Debe tener exactamente 10 dígitos numéricos</li>
                  <li><strong>Valor Negativo:</strong> Activar para secciones que representen descuentos o valores negativos</li>
                  <li><strong>Listar:</strong> Desactivar para ocultar la sección en listados y reportes</li>
                </ul>
                <div style={{ marginTop: 8 }}>
                  <strong>💡 Ejemplos comunes:</strong>
                  <ul style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                    <li><strong>HAB</strong> - Facturación de la ocupación</li>
                    <li><strong>MIN</strong> - Ingreso minibar</li>
                    <li><strong>DES</strong> - Desayuno extra (valor negativo)</li>
                    <li><strong>CEN</strong> - Cena extra (valor negativo)</li>
                    <li><strong>LAV</strong> - Ingreso lavandería</li>
                  </ul>
                </div>
              </div>
            </Card>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default Secciones;